import { bytes32Schema, hexSchema, nonzeroAddressSchema, tradeQuoteSchema, type PreparedCall, type TradeIntent } from '@hoodmorning/contracts';
import { z } from 'zod';
import { getAddress, type Hex } from 'viem';
import { hashPreparedCalls } from './calls.js';
import { decodeAndValidateV2Trade, decodeAndValidateV3Trade } from './routerTradeDecoder.js';
import { decodeAndValidateNativeV2Trade } from './nativeRouterTradeDecoder.js';
import { terminalRouterVersionForDecoder } from './terminalRouterVersion.js';

const uint256 = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/).refine(value => value.length <= 78 && /^(0|[1-9][0-9]*)$/.test(value) && BigInt(value) < 1n << 256n, 'Quantity exceeds uint256');
const storedQuote = tradeQuoteSchema.extend({ amountIn: uint256, expectedAmountOut: uint256, minimumAmountOut: uint256,
  hoodMorningFeeAmount: uint256, gasEstimate: uint256, router: nonzeroAddressSchema, feeRecipient: nonzeroAddressSchema,
  providerRequestId: z.string().min(1).max(1024) }).strict();
const legacyStoredQuote = storedQuote.extend({ routerVersion: z.literal('2.1.1') });
const legacySchema = z.strictObject({ schemaVersion: z.literal(1), executionSource: z.literal('external-terminal'),
  family: z.enum(['V2', 'V3']), decoderVersion: z.literal('5.11.3'), quote: legacyStoredQuote,
  calls: z.array(z.strictObject({ to: nonzeroAddressSchema, data: hexSchema.refine(value => value.length <= 131074, 'Calldata exceeds bound'), value: uint256 })).min(1).max(2),
  callsHash: bytes32Schema, executionReady: z.literal(false) });
const atomicApprovalSchema = legacySchema.extend({ schemaVersion: z.literal(2), calls: z.array(legacySchema.shape.calls.element).length(4) });
const nativeWrapperSchema = z.strictObject({ address: nonzeroAddressSchema,
  codeHash: bytes32Schema.refine(value => !/^0x0{64}$/i.test(value), 'Native wrapper code hash is required') });
const nativeSchema = legacySchema.extend({ schemaVersion: z.literal(3), family: z.literal('V2'),
  nativeWrapper: nativeWrapperSchema,
  calls: z.array(legacySchema.shape.calls.element).min(1).max(4).refine(calls => calls.length !== 3, 'Unsupported native call count') });
const v212Schema = legacySchema.extend({ schemaVersion: z.literal(4), decoderVersion: z.literal('5.12.0'),
  quote: storedQuote.extend({ routerVersion: z.literal('2.1.2') }),
  calls: z.array(legacySchema.shape.calls.element).min(1).max(4).refine(calls => calls.length !== 3, 'Unsupported call count') });
const nativeV212Schema = v212Schema.extend({ schemaVersion: z.literal(5), family: z.literal('V2'),
  nativeWrapper: nativeWrapperSchema });
const schema = z.discriminatedUnion('schemaVersion', [legacySchema, atomicApprovalSchema, nativeSchema, v212Schema, nativeV212Schema]);
export type TerminalQuoteRecord = z.infer<typeof schema>;
export type TerminalNativeWrapperPin = z.infer<typeof nativeWrapperSchema>;
/** Only after bounded record parsing; classification is not execution authority. */
export function isNativeTerminalQuoteRecord(record: TerminalQuoteRecord): record is Extract<TerminalQuoteRecord, { schemaVersion: 3 | 5 }> {
  return record.schemaVersion === 3 || record.schemaVersion === 5;
}
type Context = { intent: TradeIntent<4663 | 46630>; policy: Parameters<typeof decodeAndValidateV2Trade>[0]['policy'];
  /** Independently selected original release/configuration pin, never copied from the saved record as authority. */
  nativeWrapper?: TerminalNativeWrapperPin };

/** Shared bounded storage parsing, not execution validation. */
function parseStoredRecord(record: unknown) {
  const parsed = schema.parse(record);
  if (new TextEncoder().encode(JSON.stringify(parsed)).byteLength > 300000) throw new Error('Terminal quote record exceeds storage bound');
  // The strict schema above establishes the Hex representation before narrowing.
  const calls: PreparedCall[] = parsed.calls.map(call => ({ to: getAddress(call.to), data: call.data as Hex, value: BigInt(call.value) }));
  if (hashPreparedCalls(calls).toLowerCase() !== parsed.callsHash.toLowerCase()) throw new Error('Terminal quote calls hash mismatch');
  return { parsed, calls };
}

/** Historical display only: checks storage shape, bounds and call hash, not
 * semantic validity, freshness or authority. The repository must independently
 * bind user/intent/fee metadata. Never rewind an execution clock for history.
 */
export function readTerminalQuoteHistory(record: unknown) {
  const { parsed } = parseStoredRecord(record);
  return { record: parsed, executionReady: false as const, validationScope: 'storage-shape-and-call-hash-only' as const };
}

/** Execution-time restore rejects expired terms and reruns calldata validation.
 * Neither a record nor its hash proves DB authenticity, route classification,
 * chain/owner identity, simulation, approval readiness or submission permission.
 */
export function readTerminalQuoteRecord(record: unknown, context: Context) {
  const { parsed, calls } = parseStoredRecord(record);
  if (parsed.decoderVersion !== context.policy.requiredDecoderVersion
    || parsed.quote.routerVersion !== terminalRouterVersionForDecoder(context.policy.requiredDecoderVersion)) {
    throw new Error('Historical router decoder profile mismatch');
  }
  if (isNativeTerminalQuoteRecord(parsed)) {
    const pin = nativeWrapperSchema.parse(structuredClone(context.nativeWrapper));
    if (getAddress(pin.address) !== getAddress(parsed.nativeWrapper.address)
      || pin.codeHash.toLowerCase() !== parsed.nativeWrapper.codeHash.toLowerCase()) {
      throw new Error('Historical native wrapper profile mismatch');
    }
    const decoding = decodeAndValidateNativeV2Trade({ intent: structuredClone(context.intent), quote: parsed.quote,
      calls, policy: structuredClone(context.policy), nativeWrapper: getAddress(pin.address) });
    return { record: parsed, calls, decoding };
  }
  const decode = parsed.family === 'V2' ? decodeAndValidateV2Trade : decodeAndValidateV3Trade;
  const decoding = decode({ intent: structuredClone(context.intent), quote: parsed.quote, calls, policy: structuredClone(context.policy) });
  return { record: parsed, calls, decoding };
}

/** Re-derive policy proof from actual bytes before emitting a plain JSON record.
 * No provider payload, signature, API key, closure or renewed expiry is retained.
 */
export function createTerminalQuoteRecord(input: Parameters<typeof decodeAndValidateV2Trade>[0] & { family: 'V2' | 'V3' }): TerminalQuoteRecord {
  const calls = input.calls.map(call => {
    if (typeof call.value !== 'bigint' || call.value < 0n || call.value >= 1n << 256n) throw new Error('Invalid call value');
    return { to: call.to, data: call.data, value: call.value.toString() };
  });
  const routerVersion = terminalRouterVersionForDecoder(input.policy.requiredDecoderVersion);
  const record = { schemaVersion: routerVersion === '2.1.2' ? 4 : input.calls.length === 4 ? 2 : 1,
    executionSource: 'external-terminal', family: input.family, decoderVersion: input.policy.requiredDecoderVersion,
    quote: structuredClone(input.quote), calls, callsHash: hashPreparedCalls(input.calls), executionReady: false };
  return readTerminalQuoteRecord(record, { intent: input.intent, policy: input.policy }).record;
}

/** Explicit native-only record, consumed only with its original profile.
 * Pins and bytes are retained for nonrenewing restore, not authenticated merely
 * by storage. Wrapper/router code, backing, source, native cashflows, simulation,
 * gas, current owner and dispatch remain independent admission/release checks.
 * Never reinterpret a legacy ERC20 record or accept a saved pin as authority.
 */
export function createNativeTerminalQuoteRecord(input: Parameters<typeof decodeAndValidateV2Trade>[0] & {
  nativeWrapper: TerminalNativeWrapperPin;
}): Extract<TerminalQuoteRecord, { schemaVersion: 3 | 5 }> {
  const saved = structuredClone(input);
  const nativeWrapper = nativeWrapperSchema.parse(saved.nativeWrapper);
  const calls = saved.calls.map(call => {
    if (typeof call.value !== 'bigint' || call.value < 0n || call.value >= 1n << 256n) throw new Error('Invalid call value');
    return { to: call.to, data: call.data, value: call.value.toString() };
  });
  const routerVersion = terminalRouterVersionForDecoder(saved.policy.requiredDecoderVersion);
  const record = { schemaVersion: routerVersion === '2.1.1' ? 3 : 5,
    executionSource: 'external-terminal', family: 'V2', decoderVersion: saved.policy.requiredDecoderVersion,
    nativeWrapper, quote: saved.quote, calls, callsHash: hashPreparedCalls(saved.calls), executionReady: false };
  const restored = readTerminalQuoteRecord(record, { intent: saved.intent, policy: saved.policy, nativeWrapper }).record;
  if (!isNativeTerminalQuoteRecord(restored)) throw new Error('Native quote version mismatch');
  return restored;
}
